import React, { useState } from 'react';
import { ExternalLink, MapPin, Phone, Mail, ChevronDown, ChevronUp, Layers, CheckCircle2, Star } from 'lucide-react';
import { resolveMediaUrl, handleImageError } from '../../../utils/mediaResolver';

/* 1. Page Cards & Blocks Preview */
export function PageBlockPreview({ form = {} }) {
  const blockType = form.block_type || 'card';

  return (
    <div className="p-6 md:p-8 space-y-6 flex-1 flex flex-col justify-center items-center font-sans">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-md overflow-hidden">
        {/* Parent Page Context Header */}
        <div className="bg-slate-100 px-4 py-2 border-b flex items-center justify-between text-xs text-slate-600">
          <div className="flex items-center gap-1.5 font-medium">
            <Layers className="w-3.5 h-3.5 text-indigo-700" />
            <span>Target Page: <strong className="text-slate-900 font-mono">/{form.page_slug || 'unassigned'}</strong></span>
          </div>
          <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 font-bold uppercase text-[10px]">
            {blockType}
          </span>
        </div>

        {/* Media */}
        {form.image && (
          <div className="h-44 bg-slate-50 relative overflow-hidden border-b">
            <img
              src={resolveMediaUrl(form.image)}
              alt={form.alt || form.title || ''}
              className="w-full h-full object-cover"
              onError={handleImageError}
            />
          </div>
        )}

        {/* Card Content based on type */}
        <div className={`p-6 space-y-3 ${blockType === 'action' ? 'bg-[#2B3380] text-white' : blockType === 'note' ? 'bg-indigo-50/70 border-l-4 border-[#2B3380]' : ''}`}>
          {form.eyebrow && (
            <span className={`text-[10px] font-bold uppercase tracking-wider block ${blockType === 'action' ? 'text-blue-200' : 'text-[#2B3380]'}`}>
              {form.eyebrow}
            </span>
          )}

          <div className="flex items-baseline justify-between gap-2">
            <h3 className={`text-base md:text-lg font-bold ${blockType === 'action' ? 'text-white' : 'text-slate-900'}`}>
              {form.title || <span className="text-gray-300 italic">Block Title</span>}
            </h3>
            {form.status_text && (
              <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-900 font-bold text-[10px] whitespace-nowrap">
                {form.status_text}
              </span>
            )}
          </div>

          {form.description && (
            <p className={`text-xs leading-relaxed whitespace-pre-line ${blockType === 'action' ? 'text-white/80' : 'text-slate-600'}`}>
              {form.description}
            </p>
          )}

          {(form.cta_label || form.href) && (
            <div className="pt-2">
              <span className={`inline-flex items-center gap-1 text-xs font-bold ${blockType === 'action' ? 'text-white underline' : 'text-[#2B3380] hover:underline'}`}>
                {form.cta_label || 'Learn more'}
                <ExternalLink className="w-3 h-3 ml-0.5" />
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* 2. Category Preview */
export function CategoryPreview({ form = {} }) {
  return (
    <div className="p-6 md:p-8 space-y-6 flex-1 flex flex-col justify-center items-center font-sans">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl shadow-md overflow-hidden text-center p-6 space-y-4">
        <div className="w-24 h-24 rounded-2xl bg-slate-50 border mx-auto p-2 flex items-center justify-center overflow-hidden">
          {form.image ? (
            <img src={resolveMediaUrl(form.image)} alt={form.alt || form.name || ''} className="w-full h-full object-contain" onError={handleImageError} />
          ) : (
            <span className="text-3xl">🗂️</span>
          )}
        </div>

        <div>
          <h3 className="text-lg font-bold text-slate-900">
            {form.name || <span className="text-gray-300 italic">Category Name</span>}
          </h3>
          <div className="text-xs text-slate-400 font-mono mt-0.5">/{form.slug || 'category-slug'}</div>
          {form.description && (
            <p className="text-xs text-slate-600 mt-2 leading-relaxed">{form.description}</p>
          )}
        </div>

        <div className="pt-2 flex flex-wrap items-center justify-center gap-2 text-xs">
          {form.show_on_homepage && (
            <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-bold text-[10px]">
              ✓ Shows on Homepage
            </span>
          )}
          {form.parent_id && (
            <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-[10px]">
              Parent: {form.parent_id}
            </span>
          )}
        </div>

        {form.cta_label && (
          <div className="pt-2">
            <span className="px-4 py-2 rounded-lg bg-[#2B3380] text-white text-xs font-bold inline-block shadow-sm">
              {form.cta_label}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/* 3. Brand Preview */
export function BrandPreview({ form = {} }) {
  return (
    <div className="p-6 md:p-8 space-y-6 flex-1 flex flex-col justify-center items-center font-sans">
      <div className="w-full max-w-xs bg-white border border-slate-200 rounded-2xl shadow-md p-6 text-center space-y-4">
        <div className="h-20 bg-slate-50 rounded-xl border flex items-center justify-center p-3">
          {form.logo ? (
            <img src={resolveMediaUrl(form.logo)} alt={form.name || 'Brand Logo'} className="max-h-full max-w-full object-contain" onError={handleImageError} />
          ) : (
            <span className="text-slate-400 text-xs italic">Brand Logo</span>
          )}
        </div>

        <div>
          <h3 className="font-bold text-slate-900 text-base">{form.name || 'Brand Name'}</h3>
          {form.href && <span className="text-xs text-blue-700 underline font-mono block mt-1">{form.href}</span>}
        </div>

        <div className="pt-2">
          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${form.show_on_homepage ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
            {form.show_on_homepage ? '● Appears on Homepage' : '○ Internal Brand'}
          </span>
        </div>
      </div>
    </div>
  );
}

/* 4. Store Preview */
export function StorePreview({ form = {} }) {
  return (
    <div className="p-6 md:p-8 space-y-6 flex-1 flex flex-col justify-center items-center font-sans">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-md overflow-hidden">
        {form.image && (
          <div className="h-44 bg-slate-100 overflow-hidden">
            <img
              src={resolveMediaUrl(form.image)}
              alt={form.name || 'Store'}
              className="w-full h-full object-cover"
              onError={handleImageError}
            />
          </div>
        )}

        <div className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#2B3380] block">
                {form.city || 'Store City'}
              </span>
              <h3 className="text-lg font-bold text-slate-900 mt-0.5">{form.name || 'Store Name'}</h3>
            </div>
            {form.featured && (
              <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold">
                ⭐ Featured
              </span>
            )}
          </div>

          {form.address && (
            <div className="flex items-start gap-2 text-xs text-slate-600">
              <MapPin className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="whitespace-pre-line leading-relaxed">{form.address}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t text-slate-600">
            {form.phone && <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-emerald-600" /> {form.phone}</div>}
            {form.email && <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-blue-600" /> {form.email}</div>}
          </div>

          {form.href && (
            <div className="pt-2">
              <span className="w-full py-2.5 rounded-xl bg-[#2B3380] text-white text-xs font-bold flex items-center justify-center gap-1.5 shadow-sm">
                Get Directions <ExternalLink className="w-3.5 h-3.5" />
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* 5. Article Preview */
export function ArticlePreview({ form = {} }) {
  return (
    <div className="p-6 md:p-8 space-y-6 flex-1 flex flex-col justify-center items-center font-sans">
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl shadow-md overflow-hidden">
        {form.image && (
          <div className="h-52 bg-slate-100 overflow-hidden">
            <img
              src={resolveMediaUrl(form.image)}
              alt=""
              className="w-full h-full object-cover"
              onError={handleImageError}
            />
          </div>
        )}

        <div className="p-6 space-y-3">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-bold uppercase text-[10px]">
              {form.category || 'News'}
            </span>
            {form.author && <span>By {form.author}</span>}
            {form.published_at && <span>• {form.published_at}</span>}
          </div>

          <h2 className="text-xl font-bold text-slate-900 leading-snug">
            {form.title || <span className="text-gray-300 italic">Article Title</span>}
          </h2>

          {form.excerpt && (
            <p className="text-xs text-slate-600 leading-relaxed">{form.excerpt}</p>
          )}

          {form.content && (
            <div className="text-xs text-slate-700 whitespace-pre-line leading-relaxed pt-2 border-t max-h-32 overflow-y-auto">
              {form.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* 6. FAQ Preview */
export function FaqPreview({ form = {} }) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="p-6 md:p-8 space-y-6 flex-1 flex flex-col justify-center items-center font-sans">
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl shadow-md p-6 space-y-4">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold text-[10px]">
            Category: {form.category || 'General FAQ'}
          </span>
          <span className="text-[10px]">Order: #{form.display_order ?? 0}</span>
        </div>

        {/* Interactive Accordion item */}
        <div className="border rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="w-full p-4 text-left font-bold text-slate-900 text-sm flex items-center justify-between gap-3 bg-slate-50 hover:bg-slate-100 transition"
          >
            <span>{form.question || <span className="text-gray-300 italic">Frequently asked question...</span>}</span>
            {isOpen ? <ChevronUp className="w-4 h-4 text-slate-500 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />}
          </button>
          {isOpen && (
            <div className="p-4 bg-white text-xs text-slate-700 whitespace-pre-line leading-relaxed border-t">
              {form.answer || <span className="text-gray-300 italic">Answer content goes here.</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* 7. Testimonial Preview */
export function TestimonialPreview({ form = {} }) {
  const rating = Math.min(5, Math.max(1, Number(form.rating) || 5));

  return (
    <div className="p-6 md:p-8 space-y-6 flex-1 flex flex-col justify-center items-center font-sans">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-md p-6 space-y-4 text-center">
        {/* Profile Image */}
        <div className="w-16 h-16 rounded-full bg-slate-100 border mx-auto overflow-hidden flex items-center justify-center">
          {form.profile_image ? (
            <img
              src={resolveMediaUrl(form.profile_image)}
              alt=""
              className="w-full h-full object-cover"
              onError={handleImageError}
            />
          ) : (
            <span className="text-xl">👤</span>
          )}
        </div>

        {/* Star Rating */}
        <div className="flex justify-center gap-1 text-amber-400">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className={`w-4 h-4 ${i < rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
          ))}
        </div>

        {/* Review Quote */}
        <p className="text-sm text-slate-700 italic leading-relaxed">
          "{form.content || 'Great service and excellent product quality from ZOBOX!'}"
        </p>

        {/* Customer Name and Designation */}
        <div className="pt-2 border-t">
          <strong className="text-sm font-bold text-slate-900 block">
            {form.customer_name || 'Customer Name'}
          </strong>
          {form.designation && (
            <span className="text-xs text-slate-500">{form.designation}</span>
          )}
        </div>
      </div>
    </div>
  );
}
